/**
 * Keyboard Sound Section
 * Plays keyboard switch sounds from product metafields
 */

if (!customElements.get('keyboard-sound')) {
  class KeyboardSound extends HTMLElement {
    constructor() {
      super();

      this.products = [];
      this.currentProduct = null;
      this.currentVariant = null;
      this.currentSwitchIndex = 0;
      this.audio = null;
      this.isPlaying = false;
      this.isMuted = false;
      this.volume = 0.5;
      this.labels = {};

      this.init();
    }

    init() {
      this.loadProducts();
      this.loadLabels();
      this.cacheElements();
      this.bindEvents();

      // Debug: Log all products and their sounds
      console.log('Keyboard Sound Section initialized');
      console.log('Products loaded:', this.products);
      this.products.forEach((p) => {
        console.log(`Product "${p.title}" - Sounds:`, p.keyboard_sounds);
      });

      if (this.products.length > 0) {
        this.selectProduct(this.products[0].id);
      }
    }

    loadProducts() {
      const jsonScript = document.querySelector('[data-keyboard-sound-products-json]');
      if (jsonScript) {
        try {
          this.products = JSON.parse(jsonScript.textContent);
          console.log('Raw JSON parsed:', this.products);
        } catch (e) {
          console.error('Failed to parse keyboard sound products JSON:', e);
        }
      }
    }

    loadLabels() {
      const labelsScript = this.querySelector('[data-keyboard-sound-labels]');
      if (labelsScript) {
        try {
          this.labels = JSON.parse(labelsScript.textContent);
        } catch (e) {
          console.error('Failed to parse keyboard sound labels:', e);
        }
      }
    }

    cacheElements() {
      // Selectors
      this.productSelector = this.querySelector('[data-product-selector]');
      this.switchSelector = this.querySelector('[data-switch-selector]');
      this.colorSelector = this.querySelector('[data-color-selector]');
      this.switchGroup = this.querySelector('[data-switch-group]');
      this.colorGroup = this.querySelector('[data-color-group]');

      // Sound player elements
      this.soundPlayer = this.querySelector('[data-sound-player]');
      this.playBtn = this.querySelector('[data-play-btn]');
      this.muteBtn = this.querySelector('[data-mute-btn]');
      this.volumeSlider = this.querySelector('[data-volume-slider]');
      this.volumeValue = this.querySelector('[data-volume-value]');
      this.switchName = this.querySelector('[data-switch-name]');
      this.soundStatus = this.querySelector('[data-sound-status]');
      this.visualization = this.querySelector('[data-visualization]');
      this.noSoundMessage = this.querySelector('[data-no-sound]');

      // Product panel elements
      this.panelImage = this.querySelector('[data-panel-image]');
      this.panelTitle = this.querySelector('[data-panel-title]');
      this.panelVariant = this.querySelector('[data-panel-variant]');
      this.panelPrice = this.querySelector('[data-panel-price]');
      this.panelComparePrice = this.querySelector('[data-panel-compare-price]');
      this.panelStock = this.querySelector('[data-panel-stock]');
      this.panelLink = this.querySelector('[data-panel-link]');
      this.addToCartBtn = this.querySelector('[data-add-to-cart-btn]');

      // Mobile bar elements
      this.mobileBar = this.querySelector('[data-mobile-bar]');
      this.mobileImage = this.querySelector('[data-mobile-image]');
      this.mobileTitle = this.querySelector('[data-mobile-title]');
      this.mobilePrice = this.querySelector('[data-mobile-price]');
      this.mobileAddBtn = this.querySelector('[data-mobile-add-btn]');
    }

    bindEvents() {
      // Product selector
      if (this.productSelector) {
        this.productSelector.addEventListener('change', (e) => {
          this.selectProduct(parseInt(e.target.value));
        });
      }

      // Switch selector
      if (this.switchSelector) {
        this.switchSelector.addEventListener('change', (e) => {
          this.selectSwitch(e.target.value);
        });
      }

      // Color selector
      if (this.colorSelector) {
        this.colorSelector.addEventListener('change', (e) => {
          this.selectColor(e.target.value);
        });
      }

      // Play button
      if (this.playBtn) {
        this.playBtn.addEventListener('click', () => {
          this.togglePlay();
        });
      }

      // Mute button
      if (this.muteBtn) {
        this.muteBtn.addEventListener('click', () => {
          this.toggleMute();
        });
      }

      // Volume slider
      if (this.volumeSlider) {
        this.volumeSlider.addEventListener('input', (e) => {
          this.setVolume(parseInt(e.target.value) / 100);
        });
      }

      // Add to cart buttons
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', () => {
          this.addToCart(this.addToCartBtn);
        });
      }

      if (this.mobileAddBtn) {
        this.mobileAddBtn.addEventListener('click', () => {
          this.addToCart(this.mobileAddBtn);
        });
      }
    }

    selectProduct(productId) {
      this.currentProduct = this.products.find((p) => p.id === productId);
      if (!this.currentProduct) return;

      // Stop current playback
      this.stopSound();

      // Update switch selector
      this.updateSwitchSelector();

      // Update color selector
      this.updateColorSelector();

      // Find and select the first variant
      this.updateVariant();
    }

    updateSwitchSelector() {
      if (!this.switchSelector || !this.switchGroup) return;

      const switches = this.currentProduct.switches || [];

      if (switches.length > 0) {
        this.switchSelector.innerHTML = switches
          .map((sw, index) => `<option value="${sw}" data-index="${index}">${sw}</option>`)
          .join('');
        this.switchGroup.style.display = '';
      } else {
        this.switchGroup.style.display = 'none';
      }
    }

    updateColorSelector() {
      if (!this.colorSelector || !this.colorGroup) return;

      const colors = this.currentProduct.colors || [];

      if (colors.length > 0) {
        this.colorSelector.innerHTML = colors.map((color) => `<option value="${color}">${color}</option>`).join('');
        this.colorGroup.style.display = '';
      } else {
        this.colorGroup.style.display = 'none';
      }
    }

    selectSwitch(switchValue) {
      // Find switch index
      const switches = this.currentProduct.switches || [];
      this.currentSwitchIndex = switches.indexOf(switchValue);
      if (this.currentSwitchIndex === -1) this.currentSwitchIndex = 0;

      // Stop current playback and load new sound
      this.stopSound();
      this.loadSound();

      // Update variant
      this.updateVariant();
    }

    selectColor(colorValue) {
      // Update variant based on selected color
      this.updateVariant();
    }

    updateVariant() {
      if (!this.currentProduct) return;

      const selectedSwitch = this.switchSelector?.value || null;
      const selectedColor = this.colorSelector?.value || null;

      // Find matching variant
      let variant = this.currentProduct.variants.find((v) => {
        let matches = true;
        if (selectedSwitch) {
          matches =
            matches && (v.option1 === selectedSwitch || v.option2 === selectedSwitch || v.option3 === selectedSwitch);
        }
        if (selectedColor) {
          matches =
            matches && (v.option1 === selectedColor || v.option2 === selectedColor || v.option3 === selectedColor);
        }
        return matches;
      });

      // Fallback to first variant
      if (!variant) {
        variant = this.currentProduct.variants[0];
      }

      this.currentVariant = variant;

      // Update switch index based on selected switch
      if (selectedSwitch) {
        const switches = this.currentProduct.switches || [];
        this.currentSwitchIndex = switches.indexOf(selectedSwitch);
        if (this.currentSwitchIndex === -1) this.currentSwitchIndex = 0;
      }

      // Update UI
      this.updateProductPanel();
      this.updateMobileBar();
      this.loadSound();
    }

    loadSound() {
      // Get the sound URL for current switch
      const sounds = this.currentProduct?.keyboard_sounds || [];
      const soundUrl = sounds[this.currentSwitchIndex];

      // Debug logging
      console.log('Loading sound for product:', this.currentProduct?.title);
      console.log('Available sounds:', sounds);
      console.log('Current switch index:', this.currentSwitchIndex);
      console.log('Sound URL:', soundUrl);

      if (soundUrl && soundUrl.trim() !== '') {
        // Create new audio element
        if (this.audio) {
          this.audio.pause();
          this.audio = null;
        }

        this.audio = new Audio(soundUrl);
        this.audio.loop = true;
        this.audio.volume = this.isMuted ? 0 : this.volume;

        // Handle audio loading error
        this.audio.addEventListener('error', (e) => {
          console.error('Audio loading error:', e);
          console.error('Failed to load audio from:', soundUrl);
          this.showNoSound();
        });

        this.audio.addEventListener('canplaythrough', () => {
          console.log('Audio ready to play:', soundUrl);
        });

        this.audio.addEventListener('play', () => {
          this.isPlaying = true;
          this.classList.add('keyboard-sound--playing');
          this.classList.remove('keyboard-sound--no-sound');
          if (this.soundStatus) {
            this.soundStatus.textContent = this.labels.playing || 'Playing...';
          }
        });

        this.audio.addEventListener('pause', () => {
          this.isPlaying = false;
          this.classList.remove('keyboard-sound--playing');
          if (this.soundStatus) {
            this.soundStatus.textContent = this.labels.click_to_play || 'Click to play';
          }
        });

        this.audio.addEventListener('ended', () => {
          this.isPlaying = false;
          this.classList.remove('keyboard-sound--playing');
          if (this.soundStatus) {
            this.soundStatus.textContent = this.labels.click_to_play || 'Click to play';
          }
        });

        // Update switch name display
        if (this.switchName) {
          const switchValue = this.switchSelector?.value || '';
          this.switchName.textContent = switchValue;
        }

        // Show player, hide no sound message
        this.classList.remove('keyboard-sound--no-sound');
        if (this.noSoundMessage) {
          this.noSoundMessage.style.display = 'none';
        }
      } else {
        // No sound available
        console.log('No sound URL available for this switch');
        this.showNoSound();
      }
    }

    showNoSound() {
      this.audio = null;
      this.classList.add('keyboard-sound--no-sound');
      this.classList.remove('keyboard-sound--playing');
      if (this.noSoundMessage) {
        this.noSoundMessage.style.display = '';
      }
      if (this.switchName) {
        this.switchName.textContent = '';
      }
    }

    togglePlay() {
      if (!this.audio) {
        console.warn('No audio element available');
        return;
      }

      if (this.isPlaying) {
        this.audio.pause();
      } else {
        this.audio.play().catch((err) => {
          console.warn('Audio playback failed:', err);
          this.showNoSound();
        });
      }
    }

    stopSound() {
      if (this.audio) {
        this.audio.pause();
        this.audio.currentTime = 0;
      }
      this.isPlaying = false;
      this.classList.remove('keyboard-sound--playing');
    }

    toggleMute() {
      this.isMuted = !this.isMuted;

      if (this.audio) {
        this.audio.volume = this.isMuted ? 0 : this.volume;
      }

      this.classList.toggle('keyboard-sound--muted', this.isMuted);
    }

    setVolume(value) {
      this.volume = Math.max(0, Math.min(1, value));

      if (this.audio && !this.isMuted) {
        this.audio.volume = this.volume;
      }

      if (this.volumeValue) {
        this.volumeValue.textContent = `${Math.round(this.volume * 100)}%`;
      }

      // Unmute if user changes volume
      if (this.isMuted && this.volume > 0) {
        this.isMuted = false;
        this.classList.remove('keyboard-sound--muted');
        if (this.audio) {
          this.audio.volume = this.volume;
        }
      }
    }

    updateProductPanel() {
      if (!this.currentProduct || !this.currentVariant) return;

      // Update image
      if (this.panelImage) {
        this.panelImage.src = this.currentVariant.image || this.currentProduct.image;
        this.panelImage.alt = this.currentProduct.title;
      }

      // Update title
      if (this.panelTitle) {
        this.panelTitle.textContent = this.currentProduct.title;
      }

      // Update variant info
      if (this.panelVariant) {
        this.panelVariant.textContent = this.currentVariant.title !== 'Default Title' ? this.currentVariant.title : '';
      }

      // Update price
      if (this.panelPrice) {
        this.panelPrice.textContent = this.formatMoney(this.currentVariant.price);
      }

      // Update compare price
      if (this.panelComparePrice) {
        if (this.currentVariant.compare_at_price > this.currentVariant.price) {
          this.panelComparePrice.textContent = this.formatMoney(this.currentVariant.compare_at_price);
          this.panelComparePrice.style.display = '';
        } else {
          this.panelComparePrice.style.display = 'none';
        }
      }

      // Update stock status
      if (this.panelStock) {
        if (this.currentVariant.available) {
          this.panelStock.textContent = this.labels.in_stock || 'In stock';
          this.panelStock.className = 'keyboard-sound__panel-stock keyboard-sound__panel-stock--in';
        } else {
          this.panelStock.textContent = this.labels.out_of_stock || 'Out of stock';
          this.panelStock.className = 'keyboard-sound__panel-stock keyboard-sound__panel-stock--out';
        }
      }

      // Update link
      if (this.panelLink) {
        this.panelLink.href = this.currentProduct.url;
      }

      // Update add to cart button
      if (this.addToCartBtn) {
        this.addToCartBtn.dataset.variantId = this.currentVariant.id;
        this.updateAddToCartButton(this.addToCartBtn, this.currentVariant.available);
      }
    }

    updateMobileBar() {
      if (!this.currentProduct || !this.currentVariant) return;

      // Update image
      if (this.mobileImage) {
        this.mobileImage.src = this.currentVariant.image || this.currentProduct.image;
        this.mobileImage.alt = this.currentProduct.title;
      }

      // Update title
      if (this.mobileTitle) {
        this.mobileTitle.textContent = this.currentProduct.title;
      }

      // Update price
      if (this.mobilePrice) {
        this.mobilePrice.textContent = this.formatMoney(this.currentVariant.price);
      }

      // Update button
      if (this.mobileAddBtn) {
        this.mobileAddBtn.dataset.variantId = this.currentVariant.id;
        this.updateAddToCartButton(this.mobileAddBtn, this.currentVariant.available);
      }
    }

    updateAddToCartButton(button, available) {
      const btnText = button.querySelector('[data-btn-text], [data-mobile-btn-text]');

      if (available) {
        button.disabled = false;
        if (btnText) {
          btnText.textContent = this.labels.add_to_cart || 'Add to cart';
        }
      } else {
        button.disabled = true;
        if (btnText) {
          btnText.textContent = this.labels.sold_out || 'Sold out';
        }
      }

      // Reset button state
      button.classList.remove(
        'keyboard-sound__btn--loading',
        'keyboard-sound__btn--success',
        'keyboard-sound__btn--error'
      );
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      const btnText = button.querySelector('[data-btn-text], [data-mobile-btn-text]');

      // Show loading state
      button.classList.add('keyboard-sound__btn--loading');
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: parseInt(variantId),
                quantity: 1,
              },
            ],
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Success
        button.classList.remove('keyboard-sound__btn--loading');
        button.classList.add('keyboard-sound__btn--success');
        if (btnText) {
          btnText.textContent = this.labels.added_to_cart || 'Added!';
        }

        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Reset button after delay
        setTimeout(() => {
          button.classList.remove('keyboard-sound__btn--success');
          button.disabled = false;
          if (btnText) {
            btnText.textContent = this.labels.add_to_cart || 'Add to cart';
          }
        }, 2000);
      } catch (error) {
        console.error('Add to cart error:', error);

        // Show error state
        button.classList.remove('keyboard-sound__btn--loading');
        button.classList.add('keyboard-sound__btn--error');
        if (btnText) {
          btnText.textContent = 'Error';
        }

        // Reset button after delay
        setTimeout(() => {
          button.classList.remove('keyboard-sound__btn--error');
          button.disabled = false;
          if (btnText) {
            btnText.textContent = this.labels.add_to_cart || 'Add to cart';
          }
        }, 2000);
      }
    }

    formatMoney(cents) {
      // Use Shopify's money format if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }

      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return `Rp ${amount.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
    }

    disconnectedCallback() {
      // Clean up audio when element is removed
      if (this.audio) {
        this.audio.pause();
        this.audio = null;
      }
    }
  }

  customElements.define('keyboard-sound', KeyboardSound);
}
